import {
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  UserPermission
} from '@globaleris/ui.library'
import {
  DataTableColumn,
  GlobDataTable,
  RowCustomAction
} from '@globaleris/ui.library/build/components/globalerisDataTable'
import { Chip, Divider, LinearProgress, Typography } from '@material-ui/core'
import { Schedule, Visibility } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ActiveSettlementBatchModel } from '../../../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import { EnumEstadoLoteLiquidacion } from '../../../../api/oData/enums/enumEstadoLoteLiquidacion'
import { EnumTipoLoteLiquidacion } from '../../../../api/oData/enums/enumTipoLoteLiquidacion'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { formatDate } from '../../../../helpers/formatDateHelper'
import { useStyles } from './invoiceCycleBatches.styles'
import { enumEnumToValueString, enumToSelectOptionArray } from '../../../../helpers'
import { mapBatchesToTableModel } from './invoiceCycleBatches.mapper'

interface InvoiceCycleBatchesProps {
  /** true mientras se espera el primer fetch: muestra skeletons en lugar de datos vacíos */
  isLoading?: boolean
  batches: ActiveSettlementBatchModel[]
  /** Lote en foco: se muestra con barra de progreso. El resto va a la tabla */
  focusedBatchId?: number
  processedElements?: number
  totalProcessingElements?: number
  onViewProgress: (batchId: number) => void
  /** Si se informa (polling pausado), reemplaza la card del lote en curso */
  pausedContent?: React.ReactNode
  /** Intento actual del polling: se muestra cuando el lote no tiene elementos que contar */
  pollingAttempt?: { current: number; max: number }
}

export const InvoiceCycleBatches: React.FC<InvoiceCycleBatchesProps> = ({
  isLoading = false,
  batches,
  focusedBatchId,
  processedElements = 0,
  totalProcessingElements = 0,
  onViewProgress,
  pausedContent,
  pollingAttempt
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'invoiceCyclesProcessingPage'])
  const classes = useStyles()

  const focusedBatch = batches.find(batch => batch.id === focusedBatchId)

  const hasBatchProgress = totalProcessingElements > 0
  const progressPercentage = hasBatchProgress
    ? Math.round((processedElements / totalProcessingElements) * 100)
    : 0

  const batchesData = useMemo(
    () => mapBatchesToTableModel(batches, focusedBatchId),
    [batches, focusedBatchId, t]
  )

  const batchesColumns: DataTableColumn[] = [
    { name: 'id', type: DataType.NUMBER, display: false },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'type',
      label: t('invoiceCyclesProcessingPage:batches.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTipoLoteLiquidacion,
        'enums:EnumTipoLoteLiquidacion'
      )
    },
    {
      name: 'executionDateLabel',
      label: t('invoiceCyclesProcessingPage:batches.columns.executionDate'),
      type: DataType.TEXT
    },
    {
      name: 'statusLabel',
      label: t('invoiceCyclesProcessingPage:batches.columns.status'),
      type: DataType.TEXT
    }
  ]

  const rowCustomActions: RowCustomAction[] = [
    {
      icon: <Visibility fontSize="small" />,
      tooltip: t('invoiceCyclesProcessingPage:batches.actions.viewProgress'),
      onClick: (row: ActiveSettlementBatchModel) => onViewProgress(row.id),
      userPermission: UserPermission.EDIT,
      visibleIf: 'canViewProgress'
    }
  ]

  const batchesOptions: DataTableOptions = {
    search: false,
    pagination: false,
    selectableRows: false,
    rowCustomActions
  }

  if (isLoading) {
    return (
      <div className={classes.root}>
        <div className={classes.currentBatchCard}>
          <div className={classes.currentBatchHeader}>
            <Skeleton variant="rect" width={90} height={24} />
            <Skeleton variant="text" width={70} />
          </div>
          <Skeleton variant="rect" height={12} />
          <Divider />
          <div className={classes.batchDates}>
            <Skeleton variant="text" width={220} />
            <Skeleton variant="text" width={220} />
          </div>
        </div>
        <div className={classes.otherBatchesSection}>
          <DataTableSkeleton rows={3} elevation={2} />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {/* LOTE EN FOCO - key para re-disparar la animación de entrada al cambiar de lote */}
      {pausedContent}
      {!pausedContent && focusedBatch && (
        <div key={focusedBatch.id} className={classes.currentBatchCard}>
          <div className={classes.currentBatchHeader}>
            <Chip
              color="primary"
              size="small"
              label={enumEnumToValueString(EnumTipoLoteLiquidacion, focusedBatch.type)}
            />
            <div className={classes.statusLabel}>
              <span className={classes.statusDot} />
              <Typography variant="body2">
                {enumEnumToValueString(EnumEstadoLoteLiquidacion, focusedBatch.status)}
              </Typography>
            </div>
          </div>

          <div className={classes.progressRow}>
            <LinearProgress
              className={classes.progressBar}
              variant={hasBatchProgress ? 'determinate' : 'indeterminate'}
              value={hasBatchProgress ? progressPercentage : undefined}
            />
            {hasBatchProgress && (
              <Typography variant="body2" className={classes.progressValue}>
                {t('invoiceCyclesProcessingPage:processing.batchProgress', {
                  percentage: progressPercentage
                })}
              </Typography>
            )}
          </div>

          {/* Sin elementos que contar: se muestra el intento del polling de cambio de estado */}
          {!hasBatchProgress && pollingAttempt && pollingAttempt.current > 0 && (
            <Typography variant="body2" color="secondary">
              {t('invoiceCyclesProcessingPage:processing.attempt', {
                current: pollingAttempt.current,
                max: pollingAttempt.max
              })}
            </Typography>
          )}

          <Divider />

          <div className={classes.batchDates}>
            <div className={classes.batchDateRow}>
              <Schedule fontSize="small" className={classes.batchDateIcon} />
              <Typography variant="body2" className={classes.batchDateLabel}>
                {t('invoiceCyclesProcessingPage:batches.executionLabel')}:{' '}
                <span className={classes.batchDateValue}>
                  {formatDate(focusedBatch.executionDate, 'DD/MM/YYYY - HH:mm:ss')}
                </span>
              </Typography>
            </div>
          </div>
        </div>
      )}

      {/* LOTES DE LA EJECUCIÓN (el lote en foco se marca con un icono) */}
      {!pausedContent && batchesData.length > 0 && (
        <div className={classes.otherBatchesSection}>
          <CardWithTransition show={true} unmountOnExit={true}>
            <GlobDataTable
              title={
                <Typography variant="h6">
                  {t('invoiceCyclesProcessingPage:batches.batchesTitle')}
                </Typography>
              }
              data={batchesData}
              columns={batchesColumns}
              options={batchesOptions}
              rowIdField="id"
            />
          </CardWithTransition>
        </div>
      )}
    </div>
  )
}
